import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { t } from '@/locales/strings';
import { supabase } from '@/lib/supabase';
import { playConfirmDing, vibrateShort } from '@/utils/sound';
import { useScreenTransition } from '@/hooks/useScreenTransition';
import OnboardingHeader from '@/components/shared/OnboardingHeader';

const FEELINGS = ['easy', 'good', 'hard', 'exhausting', 'painful'];

// Feelings that signal the user struggled. When one of these is
// selected we surface a soft "review the foundation lesson" link
// before Continue, so a moment of doubt has a route back into the
// teaching content rather than leaving the user to find /foundation
// on their own from the menu.
const STRUGGLED_FEELINGS = new Set(['hard', 'exhausting', 'painful']);

// Inline SVG icon components — all rose #A8003F, 20×20, strokeWidth 1.6.
// One per feeling so the option cards carry a semantic visual anchor
// (rule 3 of the design system pass).
function FeelingIcon({ name }) {
  const common = {
    width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none',
    style: { flexShrink: 0, color: 'var(--primary)' },
  };
  const stroke = { stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (name === 'easy') {
    // smile arc inside a circle
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" {...stroke} />
        <path d="M8 14c1.2 1.6 2.6 2.4 4 2.4s2.8-.8 4-2.4" {...stroke} />
        <circle cx="9" cy="10" r="0.6" fill="currentColor" />
        <circle cx="15" cy="10" r="0.6" fill="currentColor" />
      </svg>
    );
  }
  if (name === 'good') {
    // heart
    return (
      <svg {...common}>
        <path d="M12 20s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 10c0 5.65-7 10-7 10z" {...stroke} />
      </svg>
    );
  }
  if (name === 'hard') {
    // lightning bolt
    return (
      <svg {...common}>
        <path d="M13 3L5 13h6l-1 8 8-10h-6l1-8z" {...stroke} />
      </svg>
    );
  }
  if (name === 'exhausting') {
    // droplet
    return (
      <svg {...common}>
        <path d="M12 3s-6 7-6 11a6 6 0 0 0 12 0c0-4-6-11-6-11z" {...stroke} />
      </svg>
    );
  }
  // painful — exclamation in circle
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="9" {...stroke} />
      <path d="M12 7v6" {...stroke} />
      <circle cx="12" cy="16.5" r="0.8" fill="currentColor" />
    </svg>
  );
}

export default function SessionFeeling() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, user } = useApp();

  const passthrough = location.state ?? {};

  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  useScreenTransition(0);

  function handleSkip() {
    navigate('/complete', { state: passthrough, replace: true });
  }

  function handleBackHome() {
    navigate('/home', { replace: true });
  }

  async function handleContinue() {
    if (!selected) return;
    setSaving(true);

    let feelingSaveError = false;
    if (user?.id && supabase) {
      // Capture the upsert outcome and thread it through to /complete
      // so the existing complete_save_error banner surfaces a failed
      // save. Without this, the user just tapped how she felt and the
      // app silently dropped it on RLS / network errors.
      try {
        const today = new Date().toISOString().slice(0, 10);
        const { error } = await supabase
          .from('diary_entries')
          .upsert(
            { user_id: user.id, entry_date: today, session_feeling: selected, updated_at: new Date().toISOString() },
            { onConflict: 'user_id,entry_date' }
          );
        if (error) feelingSaveError = true;
      } catch {
        feelingSaveError = true;
      }
    }

    // Opt-in audio + (default-on) haptic confirmation. Both gated
    // inside sound.js — safe to call unconditionally.
    playConfirmDing();
    vibrateShort();
    navigate('/complete', {
      state: {
        ...passthrough,
        // OR-merge so a failure at either step (session save in
        // SessionScreen, feeling save here) surfaces the single
        // complete_save_error message. Either kind of drop deserves
        // the same gentle notice on /complete.
        saveError: passthrough.saveError || feelingSaveError,
      },
      replace: true,
    });
  }

  return (
    <div style={{
      background: 'var(--bg)', minHeight: '100dvh', maxWidth: 520, margin: '0 auto',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Shared header: back → /home, skip → /complete (bypasses the
          feeling write so the session still closes out cleanly). */}
      <OnboardingHeader onBack={handleBackHome} onSkip={handleSkip} />

      {/* Content */}
      <div className="screen-enter" style={{ padding: '24px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Section label */}
        <div className="screen-enter-child" style={{
          fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 700, color: 'var(--warm-brown)',
          textTransform: 'uppercase', letterSpacing: '0.32em', marginBottom: 12,
        }}>
          {t('session_feeling_section_label', lang)}
        </div>

        {/* Title */}
        <h1 className="screen-enter-child" style={{
          fontFamily: 'var(--font-title)', fontStyle: 'italic', fontSize: 32, fontWeight: 500,
          color: 'var(--dark-brown)', lineHeight: 1.15, margin: '0 0 28px',
          letterSpacing: '-0.015em',
          fontVariationSettings: '"opsz" 96',
        }}>
          {t('session_feeling_title', lang)}
        </h1>

        {/* Feeling cards */}
        <div className="screen-enter-child" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FEELINGS.map((f) => {
            const isSelected = selected === f;
            return (
              <button
                type="button" key={f}
                onClick={() => setSelected(f)}
                className="tap-card"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', textAlign: 'left',
                  padding: '16px',
                  background: isSelected ? 'var(--surface-alt)' : '#fff',
                  border: `1px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 12,
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <FeelingIcon name={f} />
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontFamily: 'var(--font-body)', fontSize: 15,
                    color: 'var(--dark-brown)',
                  }}>
                    {t(`session_feeling_${f}`, lang)}
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-light)', fontSize: 13,
                    color: 'var(--warm-brown)', marginTop: 2,
                  }}>
                    {t(`session_feeling_${f}_sub`, lang)}
                  </div>
                </div>
                {isSelected && (
                  <div
                    className="motion-pop"
                    style={{
                      width: 24, height: 24, borderRadius: 12,
                      background: 'var(--primary)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, flexShrink: 0,
                    }}
                  >
                    {'\u2713'}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Spacer */}
        <div style={{ flex: 1, minHeight: 24 }} />

        {/* Review-the-lesson nudge for struggled feelings. Sits above
            Continue so a user who just selected "hard" / "painful" /
            "exhausting" can route into a refresher without losing the
            feeling she just chose: she can come back, the selection
            still stands, and Continue is unchanged. */}
        {selected && STRUGGLED_FEELINGS.has(selected) && (
          <div
            className="screen-enter-child"
            style={{
              background: 'var(--surface-alt, #F5EDE3)',
              border: '1px solid var(--border, #E8DDD4)',
              borderRadius: 12,
              padding: '14px 16px',
              marginBottom: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 14,
              color: 'var(--warm-brown)',
            }}>
              {t('session_feeling_review_prompt', lang)}
            </div>
            <button
              type="button"
              onClick={() => navigate('/foundation', { state: { from: 'session_feeling', feeling: selected } })}
              style={{
                background: 'none', border: 'none', padding: 0,
                textAlign: 'left',
                fontFamily: 'var(--font-medium)', fontSize: 15,
                color: 'var(--primary)',
                textDecoration: 'underline',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                alignSelf: 'flex-start',
              }}
            >
              {t('session_feeling_review_link', lang)}
            </button>
          </div>
        )}

        {/* Continue button */}
        {selected && (
          <button
            type="button" onClick={handleContinue}
            disabled={saving}
            className="screen-enter-child"
            style={{
              width: '100%', padding: '0 24px', height: 48,
              background: 'var(--primary)', color: '#fff',
              border: 'none', borderRadius: 24,
              fontFamily: 'var(--font-medium)', fontSize: 15,
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.7 : 1,
              WebkitTapHighlightColor: 'transparent',
              marginBottom: 'max(16px, env(safe-area-inset-bottom))',
            }}
          >
            {t('session_feeling_continue', lang)}
          </button>
        )}
      </div>
    </div>
  );
}

